import { useEffect, useState, type FormEvent } from "react";
import { Link } from "react-router-dom";

import { useLanguage } from "@/i18n/LanguageContext";
import {
  adminCreateExternalReviewer,
  adminListReviewers,
  adminRemoveReviewerById,
  adminResendTemporaryPassword,
  type ConferenceReviewer,
} from "../api";
import { formatDateTime } from "../format";
import { PageHeader } from "../shells/PageHeader";
import { PaginationBar, useClientPagination } from "../shells/pagination";

const emptyForm = {
  full_name: "",
  email: "",
  institution: "",
  phone: "",
  country: "",
  title_or_role: "",
  expertise_keywords: "",
  preferred_language: "en",
};

export function AdminReviewersPage() {
  const { t, locale } = useLanguage();
  const [rows, setRows] = useState<ConferenceReviewer[]>([]);
  const [q, setQ] = useState("");
  const [includeInactive, setIncludeInactive] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [ok, setOk] = useState<string | null>(null);
  const [addOpen, setAddOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);

  const reload = async (query = q, inactive = includeInactive) => {
    const data = await adminListReviewers(query, inactive);
    setRows(data);
  };

  useEffect(() => {
    reload().catch(() => setRows([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const pager = useClientPagination(rows, 25);

  const onSearch = async () => {
    setBusy(true);
    setError(null);
    try {
      await reload(q, includeInactive);
      pager.setPage(1);
    } catch (e) {
      setError(e instanceof Error ? e.message : t.common.error);
    } finally {
      setBusy(false);
    }
  };

  const onRemove = async (row: ConferenceReviewer) => {
    if (!row.is_active) return;
    setBusy(true);
    setError(null);
    setOk(null);
    try {
      await adminRemoveReviewerById(row.id);
      await reload();
    } catch (e) {
      setError(e instanceof Error ? e.message : t.common.error);
    } finally {
      setBusy(false);
    }
  };

  const onResendPassword = async (row: ConferenceReviewer) => {
    setBusy(true);
    setError(null);
    setOk(null);
    try {
      await adminResendTemporaryPassword(row.id);
      setOk(t.admin.resendTempPasswordDone);
      await reload();
    } catch (e) {
      setError(e instanceof Error ? e.message : t.common.error);
    } finally {
      setBusy(false);
    }
  };

  const onAddSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    setOk(null);
    try {
      const result = await adminCreateExternalReviewer({
        full_name: form.full_name.trim(),
        email: form.email.trim(),
        institution: form.institution.trim() || null,
        phone: form.phone.trim() || null,
        country: form.country.trim() || null,
        title_or_role: form.title_or_role.trim() || null,
        expertise_keywords: form.expertise_keywords.trim() || null,
        preferred_language: form.preferred_language || "en",
      });
      setOk(result.message);
      setAddOpen(false);
      setForm(emptyForm);
      await reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : t.common.error);
    } finally {
      setBusy(false);
    }
  };

  const kindLabel = (row: ConferenceReviewer) =>
    row.reviewer_kind === "external" ? t.admin.reviewerKindExternal : t.admin.reviewerKindRegistered;

  return (
    <div>
      <PageHeader title={t.admin.reviewers} lead={t.admin.reviewersLead} backTo="/admin" />
      {error && <div className="dn-alert dn-alert-error">{error}</div>}
      {ok && (
        <div className="dn-alert" style={{ background: "rgba(15,79,58,0.08)" }}>
          {ok}
        </div>
      )}
      <div className="dn-table-toolbar">
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") onSearch();
          }}
          placeholder={t.admin.searchReviewers}
          aria-label={t.admin.searchReviewers}
          style={{ flex: "1 1 16rem", minWidth: "12rem" }}
        />
        <label style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
          <input
            type="checkbox"
            checked={includeInactive}
            onChange={(e) => setIncludeInactive(e.target.checked)}
          />
          {t.admin.reviewersIncludeInactive}
        </label>
        <button type="button" className="dn-btn dn-btn-primary" disabled={busy} onClick={onSearch}>
          {t.admin.search}
        </button>
        <button type="button" className="dn-btn dn-btn-primary" disabled={busy} onClick={() => setAddOpen(true)}>
          {t.admin.addReviewer}
        </button>
        <Link className="dn-btn dn-btn-ghost" to="/admin/registrations">
          {t.admin.registrations}
        </Link>
      </div>
      <div className="dn-table-wrap">
        <table className="dn-table dn-table-responsive">
          <thead>
            <tr>
              <th>{t.admin.reviewerName}</th>
              <th>Email</th>
              <th>{t.admin.reviewerInstitution}</th>
              <th>{t.admin.reviewerKind}</th>
              <th>{t.admin.reviewerCode}</th>
              <th>{t.admin.reviewerAssignedAt}</th>
              <th>{t.admin.reviewerStatus}</th>
              <th className="dn-col-actions">{t.admin.actions}</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td colSpan={8} className="dn-muted">
                  {t.admin.reviewersEmpty}
                </td>
              </tr>
            ) : (
              pager.pageItems.map((r) => (
                <tr key={r.id}>
                  <td data-label={t.admin.reviewerName}>{r.full_name}</td>
                  <td data-label="Email">{r.email}</td>
                  <td data-label={t.admin.reviewerInstitution}>{r.institution || "—"}</td>
                  <td data-label={t.admin.reviewerKind}>
                    <span
                      className={
                        r.reviewer_kind === "external" ? "dn-badge-reviewer" : "dn-badge-reviewer"
                      }
                      style={
                        r.reviewer_kind === "external"
                          ? { background: "rgba(201,162,39,0.25)" }
                          : undefined
                      }
                    >
                      {kindLabel(r)}
                    </span>
                  </td>
                  <td data-label={t.admin.reviewerCode}>{r.registration_code || "—"}</td>
                  <td data-label={t.admin.reviewerAssignedAt}>{formatDateTime(r.assigned_at, locale)}</td>
                  <td data-label={t.admin.reviewerStatus}>
                    {r.is_active ? t.admin.reviewerActive : t.admin.reviewerInactive}
                  </td>
                  <td className="dn-col-actions" data-label={t.admin.actions}>
                    {r.is_active ? (
                      <>
                        <Link
                          className="dn-btn dn-btn-ghost"
                          to={`/admin/reviewers/${r.id}/assign-papers`}
                          style={{ marginInlineEnd: 8 }}
                        >
                          {t.admin.assignPapers}
                        </Link>
                        {r.reviewer_kind === "external" && r.must_change_password ? (
                          <button
                            type="button"
                            className="dn-btn dn-btn-ghost"
                            disabled={busy}
                            style={{ marginInlineEnd: 8 }}
                            onClick={() => onResendPassword(r)}
                          >
                            {t.admin.resendTempPassword}
                          </button>
                        ) : null}
                        <button
                          type="button"
                          className="dn-btn dn-btn-ghost"
                          disabled={busy}
                          onClick={() => onRemove(r)}
                        >
                          {t.admin.removeReviewer}
                        </button>
                      </>
                    ) : (
                      "—"
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      <PaginationBar
        page={pager.page}
        pages={pager.pages}
        from={pager.from}
        to={pager.to}
        total={pager.total}
        pageSize={pager.pageSize}
        onPage={pager.setPage}
        onPageSize={pager.setPageSize}
      />

      {addOpen && (
        <div
          className="dn-modal-backdrop"
          role="dialog"
          aria-modal="true"
          aria-label={t.admin.addReviewerTitle}
          onClick={() => !busy && setAddOpen(false)}
        >
          <div className="dn-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 520 }}>
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "0.75rem" }}>
              <h3>{t.admin.addReviewerTitle}</h3>
              <button
                type="button"
                className="dn-btn dn-btn-ghost"
                disabled={busy}
                onClick={() => setAddOpen(false)}
              >
                {t.common.close}
              </button>
            </div>
            <p className="dn-muted" style={{ marginTop: 0 }}>
              {t.admin.addReviewerLead}
            </p>
            <form className="dn-form" onSubmit={onAddSubmit}>
              <label>
                {t.admin.addReviewerFullName} *
                <input
                  value={form.full_name}
                  onChange={(e) => setForm({ ...form, full_name: e.target.value })}
                  required
                />
              </label>
              <label>
                {t.admin.addReviewerEmail} *
                <input
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  required
                />
              </label>
              <label>
                {t.admin.addReviewerInstitution}
                <input
                  value={form.institution}
                  onChange={(e) => setForm({ ...form, institution: e.target.value })}
                />
              </label>
              <label>
                {t.admin.addReviewerPhone}
                <input
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                />
              </label>
              <label>
                {t.admin.addReviewerCountry}
                <input
                  value={form.country}
                  onChange={(e) => setForm({ ...form, country: e.target.value })}
                />
              </label>
              <label>
                {t.admin.addReviewerTitleRole}
                <input
                  value={form.title_or_role}
                  onChange={(e) => setForm({ ...form, title_or_role: e.target.value })}
                />
              </label>
              <label>
                {t.admin.addReviewerExpertise}
                <textarea
                  value={form.expertise_keywords}
                  onChange={(e) => setForm({ ...form, expertise_keywords: e.target.value })}
                  rows={2}
                />
              </label>
              <label>
                {t.admin.addReviewerLanguage}
                <select
                  value={form.preferred_language}
                  onChange={(e) => setForm({ ...form, preferred_language: e.target.value })}
                >
                  <option value="en">{t.nav.langEn}</option>
                  <option value="ar">{t.nav.langAr}</option>
                  <option value="fr">{t.nav.langFr}</option>
                </select>
              </label>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                <button type="submit" className="dn-btn dn-btn-primary" disabled={busy}>
                  {t.admin.addReviewerSubmit}
                </button>
                <button
                  type="button"
                  className="dn-btn dn-btn-ghost"
                  disabled={busy}
                  onClick={() => setAddOpen(false)}
                >
                  {t.common.cancel}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
